import { reportFrontendError, reportFrontendWarning } from '../telemetry/rum'

export const AUTH0_TOKEN_KEY = 'auth0-access-token'

type AccessTokenErrorResponse = {
  status: number
  data?: unknown
}

export type Auth0TokenGetterOptions = {
  /** When true, bypass Auth0 cache and obtain a token via refresh. */
  forceRefresh?: boolean
}

type Auth0TokenGetter = (options?: Auth0TokenGetterOptions) => Promise<string>
type Auth0LogoutHandler = () => void

let auth0TokenGetter: Auth0TokenGetter | null = null
let auth0LogoutHandler: Auth0LogoutHandler | null = null
let tokenInFlight: Promise<string | null> | null = null
let forceRefreshInFlight: Promise<string | null> | null = null
let logoutInFlight = false

/** Auth0 silent-auth failures that mean the session ended — expected, not bugs. */
const EXPECTED_AUTH0_SESSION_END_CODES = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token',
  'invalid_grant',
])

const getAuth0ErrorCode = (err: unknown): string => {
  if (typeof err === 'string') {
    return err.trim().toLowerCase().replace(/\s+/g, '_')
  }
  if (!err || typeof err !== 'object') {
    return ''
  }
  const authErr = err as {
    error?: unknown
    code?: unknown
    message?: unknown
  }
  for (const value of [authErr.error, authErr.code, authErr.message]) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim().toLowerCase().replace(/\s+/g, '_')
    }
  }
  return ''
}

/** True for expected Auth0 session-end codes (e.g. login_required). */
export const isExpectedAuth0SessionEndError = (err: unknown): boolean =>
  EXPECTED_AUTH0_SESSION_END_CODES.has(getAuth0ErrorCode(err))

export const getAuth0RedirectUri = (): string =>
  `${window.location.origin}/auth/callback`

export const getAuth0LogoutReturnTo = (): string => window.location.origin

export function clearAuth0LocalStorage(): void {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i)
    if (key && key.includes('auth0')) {
      localStorage.removeItem(key)
    }
  }
}

export function replaceBrowserHistoryWithAppRoot(): void {
  window.history.replaceState(null, '', '/')
}

export function prepareAuth0Logout(): void {
  clearAuth0LocalStorage()
  replaceBrowserHistoryWithAppRoot()
}

export const registerAuth0TokenGetter = (getter: Auth0TokenGetter): void => {
  auth0TokenGetter = getter
}

export const unregisterAuth0TokenGetter = (): void => {
  auth0TokenGetter = null
  tokenInFlight = null
  forceRefreshInFlight = null
}

export const hasAuth0TokenGetter = (): boolean => auth0TokenGetter !== null

export const registerAuth0Logout = (logout: Auth0LogoutHandler): void => {
  auth0LogoutHandler = logout
}

export const unregisterAuth0Logout = (): void => {
  auth0LogoutHandler = null
  logoutInFlight = false
}

export const isAuth0LogoutInFlight = (): boolean => logoutInFlight

/**
 * Clears local Auth0 session state and runs the registered Auth0 logout
 * (or falls back to `/auth/logout`) when silent token acquisition fails.
 */
export const logoutDueToStaleSession = async (): Promise<void> => {
  if (logoutInFlight) return
  logoutInFlight = true

  try {
    try {
      localStorage.removeItem('onboardingWelcome')
      localStorage.removeItem('cloudType')
    } catch {
      // localStorage may be unavailable
    }

    try {
      const { clearAllPartnerCache } = await import('../utils/partnerDataCache')
      clearAllPartnerCache()
    } catch (err) {
      console.warn(
        'Failed to clear partner cache on stale-session logout:',
        err
      )
    }

    prepareAuth0Logout()

    if (auth0LogoutHandler) {
      auth0LogoutHandler()
      return
    }

    window.location.assign('/auth/logout')
  } catch (err) {
    reportFrontendError('api.error', err, {
      'auth.action': 'stale_session_logout',
    })
    logoutInFlight = false
  }
}

/**
 * Ideal Auth0 token acquisition for any API call:
 * - Uses Auth0 `getAccessTokenSilently` (registered getter)
 * - Auth0 returns a cached access token when still valid
 * - Auth0 uses the refresh token when the access token is expired
 * - If refresh is impossible → logout and return null
 */
export const getValidAuth0AccessToken = async (
  options: Auth0TokenGetterOptions = {}
): Promise<string | null> => {
  const { forceRefresh = false } = options

  if (!auth0TokenGetter) {
    return localStorage.getItem(AUTH0_TOKEN_KEY)
  }

  const run = async (): Promise<string | null> => {
    try {
      const token = await auth0TokenGetter!({ forceRefresh })
      localStorage.setItem(AUTH0_TOKEN_KEY, token)
      return token
    } catch (err) {
      const attributes = {
        'auth.action': forceRefresh ? 'token_refresh' : 'token_acquire',
      }
      // Expected session-end codes are Oodle noise if reported as errors;
      // the interceptor also skips telemetry once logoutInFlight is set.
      if (isExpectedAuth0SessionEndError(err)) {
        reportFrontendWarning('api.error', err, attributes)
      } else {
        reportFrontendError('api.error', err, attributes)
      }
      if (localStorage.getItem(AUTH0_TOKEN_KEY)) {
        await logoutDueToStaleSession()
      }
      return null
    }
  }

  if (forceRefresh) {
    if (!forceRefreshInFlight) {
      forceRefreshInFlight = run().finally(() => {
        forceRefreshInFlight = null
      })
    }
    return forceRefreshInFlight
  }

  if (!tokenInFlight) {
    tokenInFlight = run().finally(() => {
      tokenInFlight = null
    })
  }
  return tokenInFlight
}

/** @deprecated Prefer getValidAuth0AccessToken({ forceRefresh: true }) */
export const refreshAuth0AccessToken = async (): Promise<string | null> =>
  getValidAuth0AccessToken({ forceRefresh: true })

const collectResponseErrorText = (
  response: AccessTokenErrorResponse
): string => {
  const parts: string[] = []
  const data = response.data
  if (!data || typeof data !== 'object') {
    return ''
  }
  const payload = data as { message?: unknown; errors?: unknown }
  if (typeof payload.message === 'string') {
    parts.push(payload.message)
  }
  if (Array.isArray(payload.errors)) {
    parts.push(
      ...payload.errors.filter(
        (error): error is string => typeof error === 'string'
      )
    )
  }
  return parts.join(' ').toLowerCase()
}

/**
 * Labra APIs may reject a bad/expired token with 401, or with 403 plus an
 * "invalid access token" payload (e.g. marketplace). Plain RBAC/permission
 * 403s must not enter Auth0 recover.
 */
export const isInvalidAccessTokenResponse = (
  response?: AccessTokenErrorResponse
): boolean => {
  if (!response) return false
  if (response.status === 401) return true
  return collectResponseErrorText(response).includes('invalid access token')
}

/**
 * After an invalid-token rejection: force Auth0 silent refresh once and return
 * the new token. Logout only when silent refresh fails, or when a retried
 * request still reports a true invalid-token signal (401 / "invalid access
 * token") — not leftover permission/RBAC 403s.
 */
export const recoverFromInvalidAccessToken = async ({
  alreadyRetried,
  response,
}: {
  alreadyRetried: boolean
  response?: AccessTokenErrorResponse
}): Promise<string | null> => {
  if (alreadyRetried) {
    if (
      isInvalidAccessTokenResponse(response) &&
      localStorage.getItem(AUTH0_TOKEN_KEY)
    ) {
      await logoutDueToStaleSession()
    }
    return null
  }

  return getValidAuth0AccessToken({ forceRefresh: true })
}
